import type { PayloadFindRequestParams } from '../services/api/api.types'

export function getParamsWithDefaults(params?: PayloadFindRequestParams, defaults: PayloadFindRequestParams = {}) {
  return {
    where: params?.where ?? defaults.where ?? {},
    depth: params?.depth ?? defaults.depth ?? '0',
    sort: params?.sort ?? defaults.sort ?? '-createdAt',
    limit: params?.limit ?? defaults.limit ?? '0',
    page: params?.page ?? defaults.page ?? '1',
    select: params?.select ?? defaults.select ?? {},
    populate: params?.populate ?? defaults.populate ?? {},
  }
}

export function getHasOneRelationship<T extends { id: string }>(field: T | null | string | undefined): null | T {
  if (!field) return null
  if (typeof field === 'string') return null
  return field
}

export function getHasManyRelationships<T extends { id: string }>(field: (string | T)[] | null | undefined): T[] {
  if (!field) return []
  if (field.length === 0) return []
  if (typeof field[0] === 'string') return []
  return field as T[]
}

export function getHasOneRelationshipId<T extends { id: string }>(field: string | T | undefined | null): string | null
export function getHasOneRelationshipId<T extends { id: string }, D>(
  field: string | T | undefined | null,
  defaultValue: D,
): string | D
export function getHasOneRelationshipId<T extends { id: string }, D = null>(
  field: string | T | undefined | null,
  defaultValue?: D,
): string | D | null {
  if (typeof field === 'string') return field
  if (!!field && 'id' in field) return field.id
  return defaultValue !== undefined ? defaultValue : null
}

export function getHasManyRelationshipIds<T extends { id: string }>(
  field: (string | T)[] | null | undefined,
): string[] {
  if (!field) return []
  return field.map(item => {
    if (typeof item === 'string') return item
    return item.id
  })
}

export function getRelationshipsCount<T extends { id: string }>(field: (string | T)[] | null | undefined): number {
  if (!field) return 0
  return field.length
}

export function getHasOneJoin<T extends { id: string }>(
  field:
    | {
        docs?: (string | T)[] | null
        hasNextPage?: boolean | null
      }
    | null
    | undefined,
): null | T {
  if (!field) return null
  if (!field.docs) return null
  if (!field.docs.length) return null
  if (typeof field.docs[0] === 'string') return null
  return field.docs[0]
}

export function getHasManyJoins<T extends { id: string }>(
  field:
    | {
        docs?: (string | T)[] | null
        hasNextPage?: boolean | null
      }
    | null
    | undefined,
): T[] {
  if (!field) return []
  if (!field.docs) return []
  if (!field.docs.length) return []
  if (typeof field.docs[0] === 'string') return []
  return field.docs as T[]
}

export function getJoinsCount<T extends { id: string }>(
  field:
    | {
        docs?: (string | T)[] | null
        hasNextPage?: boolean | null
      }
    | null
    | undefined,
): number {
  if (!field) return 0
  if (!field.docs) return 0
  return field.docs.length
}

export function getHasOneJoinId<T extends { id: string }>(
  field:
    | {
        docs?: (string | T)[] | null
        hasNextPage?: boolean | null
      }
    | null
    | undefined,
): null | string {
  if (!field) return null
  if (!field.docs) return null
  if (!field.docs.length) return null
  if (typeof field.docs[0] === 'string') return field.docs[0]
  return field.docs[0].id
}

export function getHasManyJoinIds<T extends { id: string }>(
  field:
    | {
        docs?: (string | T)[] | null
        hasNextPage?: boolean | null
      }
    | null
    | undefined,
): string[] {
  if (!field) return []
  if (!field.docs) return []
  return field.docs.map(item => {
    if (typeof item === 'string') return item
    return item.id
  })
}
